import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:31000/api';
const SERVER_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:31000';

// Helper function to get full attachment URL
export const getAttachmentUrl = (filePath) => {
  // If path already starts with http, return as is
  if (filePath.startsWith('http')) {
    return filePath;
  }

  // Remove leading slash if present to avoid double slashes
  const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
  return `${SERVER_BASE_URL}/${cleanPath}`;
};

// Token management
const TOKEN_KEY = 'ticket_system_token';
const REFRESH_TOKEN_KEY = 'ticket_system_refresh_token';
const USER_KEY = 'ticket_system_user';

export const tokenManager = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (token) => localStorage.setItem(TOKEN_KEY, token),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setRefreshToken: (token) => localStorage.setItem(REFRESH_TOKEN_KEY, token),
  getUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },
  setUser: (user) => localStorage.setItem(USER_KEY, JSON.stringify(user)),
  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  isAuthenticated: () => !!tokenManager.getToken(),
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) {
        // No refresh token, logout
        tokenManager.clearAuth();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${API_BASE_URL}/users/refresh`,
          { refreshToken },
          { withCredentials: true }
        );

        const { token, refreshToken: newRefreshToken } = response.data;
        tokenManager.setToken(token);
        tokenManager.setRefreshToken(newRefreshToken);

        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        originalRequest.headers.Authorization = `Bearer ${token}`;

        processQueue(null, token);
        isRefreshing = false;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        tokenManager.clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// User APIs
export const userAPI = {
  login: async (data) => {
    const response = await api.post('/users/login', data);
    const { token, refreshToken, user } = response.data;
    tokenManager.setToken(token);
    tokenManager.setRefreshToken(refreshToken);
    tokenManager.setUser(user);
    return response;
  },

  logout: async () => {
    try {
      await api.post('/users/logout');
    } finally {
      tokenManager.clearAuth();
    }
  },

  refreshToken: () => api.post('/users/refresh'),

  getAllUsers: (params) => api.get('/users', { params }),

  getUser: (id) => api.get(`/users/${id}`),

  updateUser: (id, data) => api.patch(`/users/${id}`, data),

  assignStaff: (data) => api.post('/users/assign-staff', data),
};

// Ticket APIs
export const ticketAPI = {
  createTicket: (formData) => {
    const token = tokenManager.getToken();
    return axios.post(`${API_BASE_URL}/tickets`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });
  },

  getAllTickets: (params) => api.get('/tickets', { params }),

  getTicket: (id) => api.get(`/tickets/${id}`),

  updateTicket: (id, data) => api.patch(`/tickets/${id}`, data),

  updateStatus: (id, data) => api.patch(`/tickets/${id}/status`, data),

  withdrawTicket: (id, data) => api.patch(`/tickets/${id}/withdraw`, data),

  getUpdateHistory: (id) => api.get(`/tickets/${id}/history`),
};

// Comment APIs
export const commentAPI = {
  getComments: (ticketId) => api.get(`/tickets/${ticketId}/comments`),

  addComment: (ticketId, data) => api.post(`/tickets/${ticketId}/comments`, data),

  updateComment: (id, data) => api.patch(`/comments/${id}`, data),

  deleteComment: (id) => api.delete(`/comments/${id}`),
};

// Invite APIs
export const inviteAPI = {
  generateInvite: (data) => api.post('/invites/generate', data),

  getAllInvites: (params) => api.get('/invites', { params }),

  validateInvite: (token) => api.get(`/invites/validate/${token}`),

  registerWithInvite: (data) => api.post('/invites/register', data),

  revokeInvite: (id) => api.delete(`/invites/${id}`),
};

// Student Complaint APIs (Public - no auth required)
export const studentComplaintAPI = {
  submitComplaint: (data) => {
    // Create axios instance without auth
    // Automatically set correct Content-Type for FormData
    const headers = data instanceof FormData
      ? { 'Content-Type': 'multipart/form-data' }
      : { 'Content-Type': 'application/json' };

    return axios.post(`${API_BASE_URL}/student-complaints/submit`, data, { headers });
  },

  trackComplaint: (data) => {
    return axios.post(`${API_BASE_URL}/student-complaints/track`, data);
  },

  getAllComplaints: (params) => api.get('/student-complaints', { params }),

  updateComplaintStatus: (ticketId, data) =>
    api.patch(`/student-complaints/${ticketId}/status`, data),
};

// Complaint Type APIs
export const complaintTypeAPI = {
  getAll: () => api.get('/complaint-types'),
  create: (data) => api.post('/complaint-types', data),
};

// Password Reset APIs
export const passwordResetAPI = {
  // Admin: Generate reset link for a user
  generateResetLink: (data) => api.post('/password-reset/generate', data),

  // Admin: Get all reset tokens
  getAllResetTokens: (params) => api.get('/password-reset', { params }),

  // Admin: Revoke a reset token
  revokeResetToken: (id) => api.delete(`/password-reset/${id}`),

  // Public: Validate reset token
  validateResetToken: (token) => axios.get(`${API_BASE_URL}/password-reset/validate/${token}`),

  // Public: Reset password with token
  resetPassword: (data) => axios.post(`${API_BASE_URL}/password-reset/reset`, data),
};

export default api;
